import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle, Banknote, Calendar, CreditCard, Download, PiggyBank, Plus, RefreshCw, Save, Server,
  ShieldCheck, Trash2, TrendingDown, TrendingUp, Wallet, X
} from "lucide-react";
import {
  DailyUsageRow, FinanceSummary, FinanceTenantRow, InfraCostItem, InfraCategory, ProviderStatus,
  createInfraCost, deleteInfraCost, fetchDailyUsage, fetchFinanceSummary, fetchFinanceTenants,
  fetchProviderStatuses, syncAwsCosts, updateInfraCost, updateProviderAccount
} from "../services/api";
import { costCents, INTEGRATION_KEYS } from "../services/costRates";
import { StackedAreaChart } from "../components/charts/StackedAreaChart";
import type { IntegrationKey } from "../types";
import { useToast } from "../components/ToastProvider";
import { usePageTitle } from "../hooks/usePageTitle";

/**
 * Finance — the money view for THIS environment.
 *
 * Revenue (subscriptions billed + collected, overage, AI credit top-ups)
 * vs costs (metered COGS per tenant + fixed infrastructure like the AWS
 * boxes) = profit. Fixed infra is split evenly across paying customers;
 * each tenant's P&L row carries its allocated share.
 */

/**
 * Money formatter for the finance page. Deliberately NOT plans.ts formatZAR —
 * that helper renders 0 as "Custom" (enterprise custom pricing), which turned
 * every legitimate R0 on this page into the word "Custom".
 */
function formatZAR(cents: number): string {
  return new Intl.NumberFormat("en-ZA", { style: "currency", currency: "ZAR", maximumFractionDigits: 0 }).format(
    cents / 100
  );
}

const CATEGORY_LABELS: Record<InfraCategory, string> = {
  aws: "AWS / hosting",
  network: "Network / CDN",
  ai: "AI providers",
  comms: "Comms providers",
  tooling: "Tooling",
  other: "Other"
};

function monthOptions(): { value: string; label: string }[] {
  const out: { value: string; label: string }[] = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
    const value = d.toISOString().slice(0, 7);
    out.push({
      value,
      label: d.toLocaleDateString("en-ZA", { month: "long", year: "numeric", timeZone: "UTC" })
    });
  }
  return out;
}

type SortKey = "profit" | "revenue" | "cost" | "collected" | "name";

export function FinancePage() {
  usePageTitle("Finance");
  const toast = useToast();
  const months = useMemo(monthOptions, []);
  const [month, setMonth] = useState(months[0].value);
  const [summary, setSummary] = useState<FinanceSummary | null>(null);
  const [rows, setRows] = useState<FinanceTenantRow[]>([]);
  const [providers, setProviders] = useState<ProviderStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>("profit");
  const [sortAsc, setSortAsc] = useState(false);

  async function reload(m = month) {
    setLoading(true);
    setError(null);
    try {
      const [s, t] = await Promise.all([fetchFinanceSummary(m), fetchFinanceTenants(m)]);
      setSummary(s);
      setRows(t.tenants);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't load finance data.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void reload(month); }, [month]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchProviderStatuses().then((r) => setProviders(r.providers)).catch(() => undefined);
  }, []);

  const sorted = useMemo(() => {
    const val = (r: FinanceTenantRow): number | string => {
      switch (sortKey) {
        case "profit": return r.profitCents;
        case "revenue": return r.revenueCents;
        case "cost": return r.totalCostCents;
        case "collected": return r.collectedCents;
        case "name": return r.name.toLowerCase();
      }
    };
    return [...rows].sort((a, b) => {
      const av = val(a), bv = val(b);
      const cmp = typeof av === "string" ? String(av).localeCompare(String(bv)) : (av as number) - (bv as number);
      return sortAsc ? cmp : -cmp;
    });
  }, [rows, sortKey, sortAsc]);

  function sortBy(k: SortKey) {
    if (sortKey === k) setSortAsc(!sortAsc);
    else { setSortKey(k); setSortAsc(k === "name"); }
  }

  function downloadCsv() {
    const head = [
      "company,pack,status,billing_status,seats,per_seat_zar,monthly_zar,collected_zar,collections,failed_collections,",
      "overage_zar,credits_zar,ai_tokens,voice_minutes,whatsapp_messages,email_sends,cogs_zar,infra_share_zar,revenue_zar,profit_zar,margin_pct"
    ].join("");
    const lines = [head];
    for (const r of sorted) {
      lines.push([
        JSON.stringify(r.name), r.pack ?? r.plan, r.status, r.billingStatus, r.seats,
        (r.perSeatCents / 100).toFixed(2), (r.monthlyAmountCents / 100).toFixed(2),
        (r.collectedCents / 100).toFixed(2), r.collectionsCount, r.failedCollectionsCount,
        (r.overageBilledCents / 100).toFixed(2), (r.creditPurchaseCents / 100).toFixed(2),
        r.usage.aiTokens, r.usage.voiceMinutes, r.usage.whatsappMessages, r.usage.emailSends,
        (r.cogs.costCents / 100).toFixed(2), (r.allocatedInfraCents / 100).toFixed(2),
        (r.revenueCents / 100).toFixed(2), (r.profitCents / 100).toFixed(2),
        r.marginPct ?? ""
      ].join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `finance-${summary?.environment ?? "env"}-${month}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  if (loading && !summary) return <div className="muted">Loading finance…</div>;
  if (error && !summary) return <div className="empty-state"><h3>{error}</h3></div>;
  if (!summary) return null;

  const s = summary;
  const profit = s.profit.billedBasisCents;
  const costRows = [
    { label: "AI tokens (COGS)", cents: s.costs.cogs.openaiCents, color: "#7c5cff" },
    { label: "Voice minutes", cents: s.costs.cogs.voiceCents, color: "#00d4ff" },
    { label: "WhatsApp messages", cents: s.costs.cogs.whatsappCents, color: "#ff5ca7" },
    { label: "Email sends", cents: s.costs.cogs.emailCents, color: "#ffb547" },
    { label: "Infrastructure (fixed)", cents: s.costs.infraZarCents, color: "#2ee69b" }
  ];
  const costMax = Math.max(1, ...costRows.map((r) => r.cents));

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <span className="eyebrow">Overview</span>
          <h1>Finance</h1>
          <p className="muted">
            Revenue vs costs vs profit for the <strong>{s.environment || "local"}</strong> environment ·{" "}
            fixed infra split across {s.customers.allocationDivisor}{" "}
            {s.customers.paying > 0 ? "paying" : "active"} customer{s.customers.allocationDivisor === 1 ? "" : "s"}.
          </p>
        </div>
        <div className="page-actions row" style={{ gap: 8 }}>
          <Calendar size={14} style={{ color: "var(--text-mute)" }} />
          <select value={month} onChange={(e) => setMonth(e.target.value)}>
            {months.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
          <button className="btn btn-ghost btn-sm" onClick={downloadCsv}>
            <Download size={14} /> Export CSV
          </button>
        </div>
      </header>

      {!s.cogsKnown && (
        <div className="card" style={{ borderColor: "var(--warn)", marginBottom: 14 }}>
          <span className="muted">
            Analytics-service is unreachable — usage costs show as zero. Revenue figures are still accurate.
          </span>
        </div>
      )}

      <ProviderAlerts providers={providers} />

      {/* KPI row */}
      <div className="grid-4">
        <div className="card kpi">
          <div className="kpi-icon" style={{ background: "var(--success-soft)", color: "var(--success)" }}>
            <Banknote size={18} />
          </div>
          <h3>Revenue billed · {s.month}</h3>
          <div className="value tnum">{formatZAR(s.revenue.totalBilledCents)}</div>
          <div className="delta up"><TrendingUp size={12} /> MRR {formatZAR(s.revenue.mrrCents)}</div>
        </div>
        <div className="card kpi">
          <div className="kpi-icon"><Wallet size={18} /></div>
          <h3>Cash collected</h3>
          <div className="value tnum">{formatZAR(s.revenue.totalCollectedCents)}</div>
          <div className="delta">
            {formatZAR(s.revenue.collectedCents)} debits · {formatZAR(s.revenue.creditPurchaseCents)} credits
          </div>
        </div>
        <div className="card kpi">
          <div className="kpi-icon" style={{ background: "var(--warn-soft)", color: "var(--warn)" }}>
            <Server size={18} />
          </div>
          <h3>Total costs</h3>
          <div className="value tnum">{formatZAR(s.costs.totalCents)}</div>
          <div className="delta">
            {formatZAR(s.costs.cogs.costCents)} usage · {formatZAR(s.costs.infraZarCents)} infra
          </div>
        </div>
        <div className="card kpi">
          <div
            className="kpi-icon"
            style={profit >= 0
              ? { background: "var(--success-soft)", color: "var(--success)" }
              : { background: "var(--danger-soft)", color: "var(--danger)" }}
          >
            <PiggyBank size={18} />
          </div>
          <h3>Net profit</h3>
          <div className="value tnum" style={{ color: profit >= 0 ? "var(--success)" : "var(--danger)" }}>
            {formatZAR(profit)}
          </div>
          <div className={`delta ${profit >= 0 ? "up" : "down"}`}>
            {profit >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
            {s.profit.marginPct != null ? `${s.profit.marginPct}% margin` : "no billed revenue"}
            {" · "}cash basis {formatZAR(s.profit.collectedBasisCents)}
          </div>
        </div>
      </div>

      {/* Cost + revenue breakdown */}
      <div className="grid-2">
        <div className="card">
          <div className="section-head">
            <h2><Server size={16} /> Where the money goes</h2>
            <span className="muted">
              {s.costs.aiSpendZarCents != null && s.isCurrentMonth
                ? `metered AI spend ${formatZAR(s.costs.aiSpendZarCents)} (actual)`
                : "usage COGS + fixed infra"}
            </span>
          </div>
          <div className="col" style={{ gap: 12 }}>
            {costRows.map((r) => (
              <div key={r.label} className="meter-row">
                <div className="meter-label row" style={{ gap: 8 }}>
                  <span style={{ width: 10, height: 10, borderRadius: 2, background: r.color, flexShrink: 0 }} />
                  {r.label}
                </div>
                <div className="meter"><span style={{ width: `${(r.cents / costMax) * 100}%`, background: r.color }} /></div>
                <div className="meter-value tnum">{formatZAR(r.cents)}</div>
              </div>
            ))}
          </div>
          <div className="row between" style={{ marginTop: 14, padding: "10px 14px", background: "var(--surface)", borderRadius: "var(--radius-sm)" }}>
            <span className="muted" style={{ fontSize: 12 }}>Infra share per paying customer</span>
            <strong className="tnum">{formatZAR(s.costs.infraPerCustomerCents)} / month</strong>
          </div>
        </div>

        <div className="card">
          <div className="section-head">
            <h2><Banknote size={16} /> Where the money comes from</h2>
            <span className="muted">{s.customers.paying} paying of {s.customers.total} tenants</span>
          </div>
          <dl className="detail-list">
            <dt>Subscriptions billed</dt><dd className="tnum">{formatZAR(s.revenue.billedCents)}</dd>
            <dt>Subscriptions collected</dt><dd className="tnum">{formatZAR(s.revenue.collectedCents)}</dd>
            <dt>Overage billed</dt><dd className="tnum">{formatZAR(s.revenue.overageBilledCents)}</dd>
            <dt>AI credit top-ups</dt><dd className="tnum">{formatZAR(s.revenue.creditPurchaseCents)}</dd>
            <dt>Monthly recurring revenue</dt><dd className="tnum">{formatZAR(s.revenue.mrrCents)}</dd>
          </dl>
          <p className="muted" style={{ fontSize: 12, marginTop: 10 }}>
            Billed = what this month's subscriptions + overages + top-ups invoice.
            Collected = cash that actually landed (successful debit orders and manual mark-paids).
          </p>
        </div>
      </div>

      <ProviderCreditCard providers={providers} onChanged={setProviders} />

      <DailyUsageCard />

      <InfraCostsCard costs={s.infraCosts} onChanged={() => reload()} />

      {/* Per-tenant P&L */}
      <div className="card">
        <div className="section-head">
          <h2>Profit by company · {s.month}</h2>
          <span className="muted">revenue − usage COGS − infra share · click a column to sort</span>
        </div>
        <div className="table-wrap" style={{ border: 0 }}>
          <table className="table">
            <thead>
              <tr>
                <th onClick={() => sortBy("name")} style={{ cursor: "pointer" }}>Company</th>
                <th>Billing</th>
                <th className="tnum">Seats × per-rep</th>
                <th className="tnum">Monthly</th>
                <th className="tnum" onClick={() => sortBy("collected")} style={{ cursor: "pointer" }}>Collected</th>
                <th className="tnum">Extras</th>
                <th className="tnum" onClick={() => sortBy("cost")} style={{ cursor: "pointer" }}>Costs</th>
                <th className="tnum" onClick={() => sortBy("revenue")} style={{ cursor: "pointer" }}>Revenue</th>
                <th className="tnum" onClick={() => sortBy("profit")} style={{ cursor: "pointer" }}>Profit</th>
                <th className="tnum">Margin</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((r) => (
                <tr key={r.tenantId}>
                  <td>
                    <Link to={`/companies/${r.tenantId}`} style={{ fontWeight: 600 }}>{r.name}</Link>
                    <div className="muted" style={{ fontSize: 11 }}>{r.pack ?? r.plan}</div>
                  </td>
                  <td>
                    <span className={`pill ${r.billingStatus === "active" ? "success" : r.billingStatus === "past_due" ? "danger" : r.billingStatus === "comped" ? "accent" : "info"}`}>
                      {r.billingStatus}
                    </span>
                    {r.failedCollectionsCount > 0 && (
                      <div className="muted" style={{ fontSize: 11, color: "var(--danger)" }}>
                        {r.failedCollectionsCount} failed debit{r.failedCollectionsCount === 1 ? "" : "s"}
                      </div>
                    )}
                  </td>
                  <td className="tnum">{r.seats} × {formatZAR(r.perSeatCents)}</td>
                  <td className="tnum">{r.paying ? formatZAR(r.monthlyAmountCents) : <span className="muted">not billed</span>}</td>
                  <td className="tnum">
                    {r.collectedCents > 0 ? formatZAR(r.collectedCents) : <span className="muted">—</span>}
                  </td>
                  <td className="tnum" title="Overage + AI credit top-ups">
                    {r.overageBilledCents + r.creditPurchaseCents > 0
                      ? formatZAR(r.overageBilledCents + r.creditPurchaseCents)
                      : <span className="muted">—</span>}
                  </td>
                  <td className="tnum" title={`Usage ${formatZAR(r.cogs.costCents)} + infra ${formatZAR(r.allocatedInfraCents)}`}>
                    {formatZAR(r.totalCostCents)}
                  </td>
                  <td className="tnum">{formatZAR(r.revenueCents)}</td>
                  <td className="tnum" style={{ color: r.profitCents >= 0 ? "var(--success)" : "var(--danger)", fontWeight: 600 }}>
                    {formatZAR(r.profitCents)}
                  </td>
                  <td className="tnum muted">{r.marginPct != null ? `${r.marginPct}%` : "—"}</td>
                </tr>
              ))}
              {sorted.length === 0 && (
                <tr><td colSpan={10} className="muted" style={{ textAlign: "center", padding: 24 }}>No tenants yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ============ Infra costs editor ============ */

const EMPTY_DRAFT = { name: "", category: "aws" as InfraCategory, amount: "", currency: "ZAR" as "ZAR" | "USD", notes: "" };

function InfraCostsCard({ costs, onChanged }: { costs: InfraCostItem[]; onChanged: () => void }) {
  const toast = useToast();
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncHint, setSyncHint] = useState<string | null>(null);

  const total = costs.filter((c) => c.active).reduce((s, c) => s + c.zarCents, 0);

  async function syncAws() {
    setSyncing(true);
    setSyncHint(null);
    try {
      const r = await syncAwsCosts();
      if (r.status === "ok") {
        toast.push(
          "success",
          `AWS synced: $${r.monthToDateUsd?.toFixed(2)} month-to-date → $${r.bookedUsd?.toFixed(2)}/mo booked (${Math.round((r.shareFraction ?? 1) * 100)}% share).`
        );
        onChanged();
      } else if (r.status === "access-denied") {
        setSyncHint(
          "AWS said no: the server's IAM user needs the ce:GetCostAndUsage permission. " +
            "AWS Console → IAM → Users → orbit-media-writer → Add permissions → inline policy allowing ce:GetCostAndUsage. " +
            "Until then the AWS row keeps its manually captured amount."
        );
      } else {
        toast.push("error", r.message ?? "AWS sync failed.");
      }
    } catch (e) {
      toast.push("error", e instanceof Error ? e.message : "AWS sync failed.");
    } finally {
      setSyncing(false);
    }
  }

  async function add() {
    const amount = Number(draft.amount);
    if (!draft.name.trim() || !Number.isFinite(amount) || amount < 0) {
      toast.push("error", "Give the line item a name and a valid monthly amount.");
      return;
    }
    setBusy(true);
    try {
      await createInfraCost({
        name: draft.name.trim(),
        category: draft.category,
        amountCents: Math.round(amount * 100),
        currency: draft.currency,
        notes: draft.notes.trim() || undefined
      });
      toast.push("success", "Cost added.");
      setDraft(EMPTY_DRAFT);
      setAdding(false);
      onChanged();
    } catch (e) {
      toast.push("error", e instanceof Error ? e.message : "Couldn't add the cost.");
    } finally {
      setBusy(false);
    }
  }

  async function toggleActive(c: InfraCostItem) {
    try {
      await updateInfraCost(c.id, { active: !c.active });
      onChanged();
    } catch (e) {
      toast.push("error", e instanceof Error ? e.message : "Couldn't update the cost.");
    }
  }

  async function saveAmount(c: InfraCostItem, rand: string) {
    const amount = Number(rand);
    if (!Number.isFinite(amount) || amount < 0) { toast.push("error", "Enter a valid amount."); return; }
    try {
      await updateInfraCost(c.id, { amountCents: Math.round(amount * 100) });
      toast.push("success", "Amount saved.");
      onChanged();
    } catch (e) {
      toast.push("error", e instanceof Error ? e.message : "Couldn't update the cost.");
    }
  }

  async function remove(c: InfraCostItem) {
    if (!window.confirm(`Delete "${c.name}"?`)) return;
    try {
      await deleteInfraCost(c.id);
      toast.push("success", "Cost removed.");
      onChanged();
    } catch (e) {
      toast.push("error", e instanceof Error ? e.message : "Couldn't delete the cost.");
    }
  }

  return (
    <div className="card">
      <div className="section-head">
        <h2><Server size={16} /> Fixed infrastructure costs</h2>
        <span className="row" style={{ gap: 10 }}>
          <span className="muted">this environment only · {formatZAR(total)} / month active</span>
          <button className="btn btn-ghost btn-sm" disabled={syncing} onClick={syncAws} title="Pull month-to-date spend from AWS Cost Explorer and re-book this environment's share">
            <RefreshCw size={13} className={syncing ? "spin" : undefined} /> {syncing ? "Syncing…" : "Sync from AWS"}
          </button>
        </span>
      </div>

      {syncHint && (
        <div className="card" style={{ borderColor: "var(--warn)", background: "var(--warn-soft)", marginBottom: 10, padding: 12 }}>
          <span style={{ fontSize: 12.5 }}>{syncHint}</span>
        </div>
      )}

      {costs.length === 0 && !adding && (
        <div className="empty-state" style={{ padding: 20 }}>
          <h3>No infra costs captured yet</h3>
          <p className="muted" style={{ fontSize: 12.5 }}>
            Add the recurring bills this environment pays regardless of usage — the AWS EC2 box,
            S3, Cloudflare, domains, tooling. They get split across paying customers in the P&L.
          </p>
        </div>
      )}

      {costs.length > 0 && (
        <div className="table-wrap" style={{ border: 0 }}>
          <table className="table">
            <thead>
              <tr>
                <th>Line item</th><th>Category</th><th className="tnum">Monthly amount</th>
                <th className="tnum">≈ ZAR</th><th>Active</th><th />
              </tr>
            </thead>
            <tbody>
              {costs.map((c) => <InfraRow key={c.id} cost={c} onToggle={() => toggleActive(c)} onSave={(v) => saveAmount(c, v)} onDelete={() => remove(c)} />)}
            </tbody>
          </table>
        </div>
      )}

      {adding ? (
        <div className="row wrap" style={{ gap: 10, marginTop: 12, alignItems: "flex-end" }}>
          <label style={{ flex: "1 1 180px" }}>
            Name
            <input value={draft.name} placeholder="e.g. AWS EC2 app box" onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
          </label>
          <label>
            Category
            <select value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value as InfraCategory })}>
              {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </label>
          <label style={{ width: 130 }}>
            Amount / month
            <input type="number" min={0} step="0.01" value={draft.amount} placeholder="e.g. 1450" onChange={(e) => setDraft({ ...draft, amount: e.target.value })} />
          </label>
          <label>
            Currency
            <select value={draft.currency} onChange={(e) => setDraft({ ...draft, currency: e.target.value as "ZAR" | "USD" })}>
              <option value="ZAR">ZAR</option>
              <option value="USD">USD</option>
            </select>
          </label>
          <label style={{ flex: "1 1 160px" }}>
            Notes
            <input value={draft.notes} placeholder="optional" onChange={(e) => setDraft({ ...draft, notes: e.target.value })} />
          </label>
          <button className="btn btn-primary" disabled={busy} onClick={add}><Save size={14} /> Add</button>
          <button className="btn btn-ghost" disabled={busy} onClick={() => { setAdding(false); setDraft(EMPTY_DRAFT); }}><X size={14} /></button>
        </div>
      ) : (
        <div style={{ marginTop: 12 }}>
          <button className="btn btn-ghost" onClick={() => setAdding(true)}><Plus size={14} /> Add cost line</button>
        </div>
      )}
      <p className="muted" style={{ fontSize: 12, marginTop: 10 }}>
        USD amounts convert at a fixed R19/USD (same rate as the usage COGS calculators).
      </p>
    </div>
  );
}

/* ============ Provider credit monitor ============ */

const LEVEL_STYLES: Record<string, { pill: string; label: string }> = {
  ok: { pill: "success", label: "Healthy" },
  warning: { pill: "warning", label: "Running low" },
  error: { pill: "danger", label: "Critically low" },
  unknown: { pill: "info", label: "Unknown" }
};

function money(cents: number | null, currency: string): string {
  if (cents == null) return "—";
  const amount = (cents / 100).toFixed(2);
  return currency === "USD" ? `$${amount}` : currency === "ZAR" ? `R${amount}` : `${amount} ${currency}`;
}

/** Red/amber banner shown above the KPIs whenever a provider account is low. */
function ProviderAlerts({ providers }: { providers: ProviderStatus[] }) {
  const bad = providers.filter((p) => p.active && (p.status === "error" || p.status === "warning"));
  if (bad.length === 0) return null;
  return (
    <div className="col" style={{ gap: 8, marginBottom: 14 }}>
      {bad.map((p) => (
        <div
          key={p.id}
          className="card row"
          style={{
            gap: 10,
            alignItems: "center",
            borderColor: p.status === "error" ? "var(--danger)" : "var(--warn)",
            background: p.status === "error" ? "var(--danger-soft)" : "var(--warn-soft)"
          }}
        >
          <AlertTriangle size={16} style={{ color: p.status === "error" ? "var(--danger)" : "var(--warn)", flexShrink: 0 }} />
          <div>
            <strong>{p.label}</strong>{" "}
            {p.kind === "balance_api"
              ? <>balance is down to <strong className="tnum">{money(p.remainingCents, p.currency)}</strong></>
              : <>has <strong className="tnum">{money(p.remainingCents, p.currency)}</strong> of its monthly budget left</>}
            {p.status === "error" ? " — top up now before calls/messages start failing." : " — consider topping up soon."}
          </div>
        </div>
      ))}
    </div>
  );
}

function ProviderCreditCard({ providers, onChanged }: {
  providers: ProviderStatus[];
  onChanged: (p: ProviderStatus[]) => void;
}) {
  const toast = useToast();
  if (providers.length === 0) return null;

  async function save(id: string, patchBody: Parameters<typeof updateProviderAccount>[1]) {
    try {
      const r = await updateProviderAccount(id, patchBody);
      onChanged(r.providers);
      toast.push("success", "Saved.");
    } catch (e) {
      toast.push("error", e instanceof Error ? e.message : "Couldn't save.");
    }
  }

  return (
    <div className="card">
      <div className="section-head">
        <h2><CreditCard size={16} /> Provider credit</h2>
        <span className="muted">live balances + budgets · warnings fire when remaining hits your thresholds</span>
      </div>
      <div className="grid-2">
        {providers.map((p) => {
          const style = LEVEL_STYLES[p.status] ?? LEVEL_STYLES.unknown;
          const pctUsed =
            p.kind === "budget" && p.monthlyBudgetCents && p.monthlyBudgetCents > 0 && p.spendCents != null
              ? Math.min(100, (p.spendCents / p.monthlyBudgetCents) * 100)
              : null;
          return (
            <div key={p.id} className="card" style={{ background: "var(--surface)" }}>
              <div className="row between" style={{ marginBottom: 8 }}>
                <strong className="row" style={{ gap: 8 }}>
                  {p.status === "ok" ? <ShieldCheck size={15} style={{ color: "var(--success)" }} /> : <AlertTriangle size={15} style={{ color: p.status === "error" ? "var(--danger)" : p.status === "warning" ? "var(--warn)" : "var(--text-mute)" }} />}
                  {p.label}
                </strong>
                <span className={`pill ${style.pill}`}>{style.label}</span>
              </div>

              <div className="row" style={{ gap: 24, flexWrap: "wrap", marginBottom: 8 }}>
                <div>
                  <div className="muted" style={{ fontSize: 11.5 }}>
                    {p.kind === "balance_api" ? "Balance now" : "Budget remaining"}
                  </div>
                  <div className="tnum" style={{ fontSize: 22, fontWeight: 700, color: p.status === "error" ? "var(--danger)" : p.status === "warning" ? "var(--warn)" : "var(--text)" }}>
                    {money(p.remainingCents, p.currency)}
                  </div>
                  {p.remainingZarCents != null && p.currency !== "ZAR" && (
                    <div className="muted" style={{ fontSize: 11 }}>≈ {formatZAR(p.remainingZarCents)}</div>
                  )}
                </div>
                {p.kind === "budget" && (
                  <div>
                    <div className="muted" style={{ fontSize: 11.5 }}>Spent this month</div>
                    <div className="tnum" style={{ fontSize: 22, fontWeight: 700 }}>{money(p.spendCents, p.currency)}</div>
                    {pctUsed != null && <div className="muted" style={{ fontSize: 11 }}>{pctUsed.toFixed(0)}% of budget</div>}
                  </div>
                )}
              </div>

              {pctUsed != null && (
                <div style={{ height: 6, background: "var(--bg)", borderRadius: 3, overflow: "hidden", marginBottom: 10 }}>
                  <div style={{ width: `${pctUsed}%`, height: "100%", background: pctUsed > 90 ? "var(--danger)" : pctUsed > 75 ? "var(--warn)" : "var(--success)" }} />
                </div>
              )}

              <p className="muted" style={{ fontSize: 11.5, marginTop: 0 }}>{p.detail}</p>

              <ProviderThresholds provider={p} onSave={save} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ProviderThresholds({ provider, onSave }: {
  provider: ProviderStatus;
  onSave: (id: string, patch: Parameters<typeof updateProviderAccount>[1]) => Promise<void>;
}) {
  const [warn, setWarn] = useState((provider.warnThresholdCents / 100).toFixed(2));
  const [error, setError] = useState((provider.errorThresholdCents / 100).toFixed(2));
  const [budget, setBudget] = useState(
    provider.monthlyBudgetCents != null ? (provider.monthlyBudgetCents / 100).toFixed(2) : ""
  );
  const toCents = (v: string): number | null => {
    const n = Number(v);
    return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : null;
  };
  const dirty =
    toCents(warn) !== provider.warnThresholdCents ||
    toCents(error) !== provider.errorThresholdCents ||
    (provider.kind === "budget" && toCents(budget) !== provider.monthlyBudgetCents);

  return (
    <div className="row" style={{ gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
      {provider.kind === "budget" && (
        <label style={{ width: 120 }}>
          <span className="muted" style={{ fontSize: 11 }}>Budget / mo ({provider.currency})</span>
          <input type="number" min={0} step="0.01" value={budget} onChange={(e) => setBudget(e.target.value)} />
        </label>
      )}
      <label style={{ width: 110 }}>
        <span className="muted" style={{ fontSize: 11 }}>Warn at ({provider.currency})</span>
        <input type="number" min={0} step="0.01" value={warn} onChange={(e) => setWarn(e.target.value)} />
      </label>
      <label style={{ width: 110 }}>
        <span className="muted" style={{ fontSize: 11 }}>Error at ({provider.currency})</span>
        <input type="number" min={0} step="0.01" value={error} onChange={(e) => setError(e.target.value)} />
      </label>
      {dirty && (
        <button
          className="btn btn-primary btn-sm"
          onClick={() => {
            const w = toCents(warn), e = toCents(error), b = toCents(budget);
            void onSave(provider.id, {
              warnThresholdCents: w ?? provider.warnThresholdCents,
              errorThresholdCents: e ?? provider.errorThresholdCents,
              ...(provider.kind === "budget" ? { monthlyBudgetCents: budget.trim() === "" ? null : b } : {})
            });
          }}
        >
          <Save size={13} /> Save
        </button>
      )}
    </div>
  );
}

/* ============ Daily platform usage ============ */

function DailyUsageCard() {
  const [days, setDays] = useState<DailyUsageRow[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const end = new Date();
    const start = new Date();
    start.setUTCDate(start.getUTCDate() - 29);
    fetchDailyUsage(start.toISOString(), end.toISOString())
      .then((r) => setDays(r.days))
      .catch(() => setFailed(true));
  }, []);

  const chart = useMemo(() => {
    if (!days) return [];
    const byDate = new Map(days.map((d) => [d.day, d]));
    const rows: { day: string; cents: number; byIntegration: Record<IntegrationKey, number> }[] = [];
    for (let i = 29; i >= 0; i--) {
      const d = new Date(Date.now() - i * 86400000).toISOString().slice(0, 10);
      const r = byDate.get(d);
      const byIntegration: Record<IntegrationKey, number> = {
        openai: costCents("openai", r?.aiTokens ?? 0),
        twilio_voice: costCents("twilio_voice", r?.voiceMinutes ?? 0),
        twilio_recording: 0,
        twilio_whatsapp: costCents("twilio_whatsapp", r?.whatsappMessages ?? 0),
        sendgrid: costCents("sendgrid", r?.emailSends ?? 0)
      };
      rows.push({
        day: d,
        cents: byIntegration.openai + byIntegration.twilio_voice + byIntegration.twilio_whatsapp + byIntegration.sendgrid,
        byIntegration
      });
    }
    return rows;
  }, [days]);

  const total = chart.reduce((s, r) => s + r.cents, 0);

  return (
    <div className="card">
      <div className="section-head">
        <h2><TrendingUp size={16} /> Platform usage cost per day</h2>
        <span className="muted">
          last 30 days · all tenants · {formatZAR(total)} total
        </span>
      </div>
      {failed ? (
        <div className="empty-state" style={{ padding: 18 }}>
          <h3>Daily usage unavailable</h3>
          <p className="muted" style={{ fontSize: 12.5 }}>Analytics-service didn't answer — try a reload.</p>
        </div>
      ) : !days ? (
        <div className="muted">Loading daily usage…</div>
      ) : (
        <>
          <StackedAreaChart data={chart} series={INTEGRATION_KEYS} />
          <p className="muted" style={{ fontSize: 11.5, marginBottom: 0 }}>
            Costed at COGS rates (AI tokens, voice minutes, WhatsApp messages, emails). Daily history
            accrues from the day this shipped — earlier days show zero even though monthly totals are complete.
          </p>
        </>
      )}
    </div>
  );
}

function InfraRow({ cost, onToggle, onSave, onDelete }: {
  cost: InfraCostItem;
  onToggle: () => void;
  onSave: (rand: string) => void;
  onDelete: () => void;
}) {
  const [rand, setRand] = useState((cost.amountCents / 100).toFixed(2));
  const dirty = Math.round(Number(rand) * 100) !== cost.amountCents;
  return (
    <tr style={{ opacity: cost.active ? 1 : 0.55 }}>
      <td>
        <strong>{cost.name}</strong>
        {cost.notes && <div className="muted" style={{ fontSize: 11 }}>{cost.notes}</div>}
      </td>
      <td className="muted">{CATEGORY_LABELS[cost.category] ?? cost.category}</td>
      <td className="tnum">
        <div className="row" style={{ gap: 6, justifyContent: "flex-end" }}>
          <input
            type="number"
            min={0}
            step="0.01"
            value={rand}
            onChange={(e) => setRand(e.target.value)}
            style={{ width: 110, textAlign: "right" }}
          />
          <span className="muted" style={{ fontSize: 12 }}>{cost.currency}</span>
          {dirty && (
            <button className="icon-btn" title="Save amount" onClick={() => onSave(rand)}><Save size={13} /></button>
          )}
        </div>
      </td>
      <td className="tnum">{formatZAR(cost.zarCents)}</td>
      <td>
        <label className="row" style={{ gap: 6, cursor: "pointer" }}>
          <input type="checkbox" checked={cost.active} onChange={onToggle} />
          <span className="muted" style={{ fontSize: 12 }}>{cost.active ? "counted" : "ignored"}</span>
        </label>
      </td>
      <td style={{ textAlign: "right" }}>
        <button className="icon-btn" title="Delete" onClick={onDelete}><Trash2 size={14} /></button>
      </td>
    </tr>
  );
}
